import {
    collection,
    getDocs,
    limit as fbLimit,
    orderBy,
    query,
    startAfter,
    type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { FLIGHT_LOG_PAGE_SIZE, toDate, type SpyReport } from '@kaeliux/shared';
import { getSelectedUniverseId } from '../store/universeStore';

/**
 * Espionage-report reads (real backend flavour) — direct Firestore query,
 * same rationale as `flightLog.service.ts`: a spy report is an immutable
 * record written once by `/getFleets`/`/sendFleet`/`/recallFleet`'s lazy
 * arrival processing (`apps/functions/src/fleets.ts`), owner-readable per
 * the existing `players/{userId}/{document=**}` rule.
 */

export type SpyReportCursor = QueryDocumentSnapshot | null;

export interface SpyReportPage {
    reports: SpyReport[];
    nextCursor: SpyReportCursor;
}

function normalizeSpyReport(id: string, data: Record<string, unknown>): SpyReport {
    return { ...data, id, capturedAt: toDate(data.capturedAt, new Date(0)) } as SpyReport;
}

/** One reverse-chronological page of the player's captured espionage reports. */
export async function getSpyReports(
    userId: string,
    cursor: SpyReportCursor = null,
    pageSize: number = FLIGHT_LOG_PAGE_SIZE,
): Promise<SpyReportPage> {
    const q = query(
        collection(db, 'universes', getSelectedUniverseId(), 'players', userId, 'spyReports'),
        orderBy('capturedAt', 'desc'),
        ...(cursor ? [startAfter(cursor)] : []),
        fbLimit(pageSize),
    );
    const snap = await getDocs(q);
    return {
        reports: snap.docs.map((d) => normalizeSpyReport(d.id, d.data())),
        nextCursor: snap.docs.length === pageSize ? snap.docs[snap.docs.length - 1]! : null,
    };
}
